import {
  Alert,
  Box,
  Button,
  Link as MuiLink,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { PageState } from '../../shared/data/PageState';
import { DetailHeader } from '../../shared/detail/DetailHeader';
import { MetadataList, type MetadataItem } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { toPageError } from '../partners/PartnerListPage';
import { formatWalletMoney } from '../wallets/types';
import { DeliveryAttemptsPanel } from './DeliveryAttemptsPanel';
import { withdrawalsRepositoryToken } from './repository';
import type { WithdrawalDetail } from './types';
import {
  formatReservedAge,
  formatWithdrawalMoney,
  maskWithdrawalIdentifier,
  safeMaskedDiagnostic,
} from './types';
import { WithdrawalStatusChip } from './WithdrawalListPage';
import { WithdrawalResolutionDialog } from './WithdrawalResolutionDialog';
import { WithdrawalRetryDialog } from './WithdrawalRetryDialog';
import { WithdrawalTimeline } from './WithdrawalTimeline';

export function WithdrawalDetailPage({ dataSource }: { readonly dataSource: DataSource }) {
  const { withdrawalId = '' } = useParams();
  const repository = useRepository(withdrawalsRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [tab, setTab] = useState<'overview' | 'timeline' | 'attempts'>('overview');
  const [retryOpen, setRetryOpen] = useState(false);
  const [resolutionOpen, setResolutionOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    (signal) => repository.get(withdrawalId, signal),
    [refreshKey, repository, withdrawalId],
    () => false,
  );
  const permissions = new Set(auth.status === 'authenticated' ? auth.principal.permissions : []);
  const closeAction = (action: 'retry' | 'resolution', completed: boolean) => {
    if (action === 'retry') setRetryOpen(false);
    else setResolutionOpen(false);
    if (completed) setRefreshKey((value) => value + 1);
  };

  return (
    <Box sx={{ minWidth: 0 }}>
      <PageState
        error={toPageError(resource.error, t('withdrawals.errors.detail'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <DetailHeader
              backTo={paths.affiliateWithdrawals}
              dataSource={dataSource}
              subtitle={`${resource.data.partner.code} · ${resource.data.username}`}
              title={resource.data.partnerWithdrawalId}
            />
            {resource.data.status === 'CREDIT_STATUS_UNKNOWN' ||
            resource.data.status === 'MANUAL_REVIEW' ? (
              <Alert severity="error">{t('withdrawals.exceptionAction')}</Alert>
            ) : null}
            {(permissions.has('WITHDRAWAL_RETRY') && retryableStatuses.has(resource.data.status)) ||
            (permissions.has('WITHDRAWAL_RESOLVE') && resource.data.status === 'MANUAL_REVIEW') ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                {permissions.has('WITHDRAWAL_RETRY') &&
                retryableStatuses.has(resource.data.status) ? (
                  <Button onClick={() => setRetryOpen(true)} variant="outlined">
                    {t('withdrawals.actions.retry')}
                  </Button>
                ) : null}
                {permissions.has('WITHDRAWAL_RESOLVE') &&
                resource.data.status === 'MANUAL_REVIEW' ? (
                  <Button
                    color="warning"
                    onClick={() => setResolutionOpen(true)}
                    variant="contained"
                  >
                    {t('withdrawals.actions.resolve')}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
            <WithdrawalRetryDialog
              dataSource={dataSource}
              onClose={(completed) => closeAction('retry', completed)}
              open={retryOpen}
              withdrawal={resource.data}
            />
            <WithdrawalResolutionDialog
              canViewApproval={permissions.has('APPROVAL_DECIDE')}
              dataSource={dataSource}
              onClose={(completed) => closeAction('resolution', completed)}
              open={resolutionOpen}
              withdrawal={resource.data}
            />
            <Tabs
              aria-label={t('withdrawals.detail.tabsLabel')}
              onChange={(_event, value) => setTab(value as typeof tab)}
              value={tab}
              variant="scrollable"
            >
              <Tab
                aria-controls="withdrawal-panel-overview"
                id="withdrawal-tab-overview"
                label={t('withdrawals.detail.overview')}
                value="overview"
              />
              <Tab
                aria-controls="withdrawal-panel-timeline"
                id="withdrawal-tab-timeline"
                label={t('withdrawals.detail.timeline')}
                value="timeline"
              />
              <Tab
                aria-controls="withdrawal-panel-attempts"
                id="withdrawal-tab-attempts"
                label={t('withdrawals.detail.attempts')}
                value="attempts"
              />
            </Tabs>
            <Box
              aria-labelledby="withdrawal-tab-overview"
              hidden={tab !== 'overview'}
              id="withdrawal-panel-overview"
              role="tabpanel"
            >
              {tab === 'overview' ? (
                <WithdrawalOverview
                  language={language}
                  permissions={permissions}
                  withdrawal={resource.data}
                />
              ) : null}
            </Box>
            <Box
              aria-labelledby="withdrawal-tab-timeline"
              hidden={tab !== 'timeline'}
              id="withdrawal-panel-timeline"
              role="tabpanel"
            >
              {tab === 'timeline' ? (
                <WithdrawalTimeline language={language} withdrawalId={resource.data.id} />
              ) : null}
            </Box>
            <Box
              aria-labelledby="withdrawal-tab-attempts"
              hidden={tab !== 'attempts'}
              id="withdrawal-panel-attempts"
              role="tabpanel"
            >
              {tab === 'attempts' ? (
                <DeliveryAttemptsPanel language={language} withdrawalId={resource.data.id} />
              ) : null}
            </Box>
          </Stack>
        )}
      </PageState>
    </Box>
  );
}

const retryableStatuses = new Set<WithdrawalDetail['status']>([
  'RETRY_PENDING',
  'CREDIT_STATUS_UNKNOWN',
  'MANUAL_REVIEW',
]);

function WithdrawalOverview({
  language,
  permissions,
  withdrawal,
}: {
  readonly language: TimeLanguage;
  readonly permissions: ReadonlySet<string>;
  readonly withdrawal: WithdrawalDetail;
}) {
  const { t } = useTranslation();
  const entity = (permission: string, path: string, label: string, value: ReactNode): ReactNode =>
    permissions.has(permission) ? (
      <MuiLink component={Link} to={path}>
        {label} · {value}
      </MuiLink>
    ) : (
      value
    );
  const agent = withdrawal.initiatingAgent;
  const identity: readonly MetadataItem[] = [
    { label: t('withdrawals.fields.partnerWithdrawalId'), value: withdrawal.partnerWithdrawalId },
    { label: t('withdrawals.fields.internalId'), value: withdrawal.id },
    { label: t('withdrawals.fields.transferId'), value: withdrawal.transferId },
    {
      label: t('withdrawals.fields.partnerCreditId'),
      value: maskWithdrawalIdentifier(withdrawal.partnerCreditId),
    },
    {
      label: t('withdrawals.fields.user'),
      value: entity(
        'MEMBER_VIEW',
        `${paths.members}/${withdrawal.memberId}`,
        t('withdrawals.links.member'),
        withdrawal.username,
      ),
    },
    {
      label: t('withdrawals.fields.partnerUserId'),
      value: withdrawal.partnerUserId ?? t('common.notAvailable'),
    },
    {
      label: t('withdrawals.fields.partner'),
      value: entity(
        'PARTNER_VIEW',
        `${paths.partners}/${withdrawal.partner.id}`,
        t('withdrawals.links.partner'),
        withdrawal.partner.code,
      ),
    },
    {
      label: t('withdrawals.fields.agent'),
      value:
        agent === null
          ? t('common.notAvailable')
          : entity(
              'AGENT_VIEW',
              `${paths.agents}/${agent.id}`,
              t('withdrawals.links.agent'),
              agent.code,
            ),
    },
    {
      label: t('withdrawals.fields.walletId'),
      value: entity(
        'WALLET_VIEW',
        `${paths.affiliateWallets}/${withdrawal.walletId}`,
        t('withdrawals.links.wallet'),
        withdrawal.walletId,
      ),
    },
    { label: t('withdrawals.fields.amount'), value: formatWithdrawalMoney(withdrawal) },
    {
      label: t('withdrawals.fields.status'),
      value: <WithdrawalStatusChip status={withdrawal.status} />,
    },
    {
      label: t('withdrawals.fields.reservedAge'),
      value: formatReservedAge(withdrawal.reservationAgeSeconds),
    },
    { label: t('withdrawals.fields.attempts'), value: withdrawal.attemptCount },
    { label: t('withdrawals.fields.version'), value: withdrawal.version },
    { label: t('withdrawals.fields.events'), value: withdrawal.stateEventCount },
  ];
  const operational: readonly MetadataItem[] = [
    {
      label: t('withdrawals.fields.failureCode'),
      value: withdrawal.failureCode ?? t('common.notAvailable'),
    },
    {
      label: t('withdrawals.fields.failureDetail'),
      value: safeMaskedDiagnostic(withdrawal.failureDetail),
    },
    {
      label: t('withdrawals.fields.deliveryResult'),
      value: withdrawal.latestDelivery?.normalizedResult ?? t('common.notAvailable'),
    },
    {
      label: t('withdrawals.fields.lastResponse'),
      value: safeMaskedDiagnostic(withdrawal.latestDelivery?.errorDetail ?? null),
    },
    {
      label: t('withdrawals.fields.latestAttempt'),
      value:
        withdrawal.latestDelivery === null
          ? t('common.notAvailable')
          : `#${withdrawal.latestDelivery.attemptNumber} · ${withdrawal.latestDelivery.operation} · ${maskWithdrawalIdentifier(withdrawal.latestDelivery.partnerCreditId)}`,
    },
    { label: t('withdrawals.fields.createdAt'), value: withOffset(withdrawal.createdAt, language) },
    { label: t('withdrawals.fields.updatedAt'), value: withOffset(withdrawal.updatedAt, language) },
    {
      label: t('withdrawals.fields.reservedAt'),
      value: nullableTime(withdrawal.reservedAt, language, t('common.notAvailable')),
    },
    {
      label: t('withdrawals.fields.completedAt'),
      value: nullableTime(withdrawal.completedAt, language, t('common.notAvailable')),
    },
    {
      label: t('withdrawals.fields.releasedAt'),
      value: nullableTime(withdrawal.releasedAt, language, t('common.notAvailable')),
    },
  ];
  const idempotency: readonly MetadataItem[] = [
    {
      label: t('withdrawals.fields.idempotencyId'),
      value: maskWithdrawalIdentifier(withdrawal.idempotency.id),
    },
    {
      label: t('withdrawals.fields.idempotencyKey'),
      value: maskWithdrawalIdentifier(withdrawal.idempotency.key),
    },
    { label: t('withdrawals.fields.idempotencyState'), value: withdrawal.idempotency.state },
    {
      label: t('withdrawals.fields.responseStatus'),
      value: withdrawal.idempotency.responseStatus ?? t('common.notAvailable'),
    },
    {
      label: t('withdrawals.fields.createdAt'),
      value: withOffset(withdrawal.idempotency.createdAt, language),
    },
    {
      label: t('withdrawals.fields.completedAt'),
      value: nullableTime(withdrawal.idempotency.completedAt, language, t('common.notAvailable')),
    },
    {
      label: t('withdrawals.fields.expiresAt'),
      value: withOffset(withdrawal.idempotency.expiresAt, language),
    },
  ];
  return (
    <Stack spacing={2}>
      <Section title={t('withdrawals.detail.identity')}>
        <MetadataList items={identity} />
      </Section>
      <Section title={t('withdrawals.detail.operational')}>
        <MetadataList items={operational} />
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('withdrawals.receiptGap')}
        </Alert>
      </Section>
      <Section title={t('withdrawals.detail.idempotency')}>
        <MetadataList items={idempotency} />
      </Section>
      <Section title={t('withdrawals.detail.walletPostings')}>
        <Stack spacing={1}>
          {withdrawal.walletPostings.map((posting) => (
            <Paper key={posting.entryId} sx={{ p: 1.5 }} variant="outlined">
              <Stack spacing={0.5}>
                {permissions.has('WALLET_VIEW') ? (
                  <MuiLink component={Link} to={`${paths.affiliateWallets}/${withdrawal.walletId}`}>
                    {t('withdrawals.links.ledger')} · {posting.entryId}
                  </MuiLink>
                ) : (
                  <Typography>{posting.entryId}</Typography>
                )}
                <Typography variant="body2">
                  {posting.entryType} ·{' '}
                  {formatWalletMoney(
                    posting.availableDelta,
                    withdrawal.currencyCode,
                    withdrawal.currencyMinorUnit,
                  )}{' '}
                  ·{' '}
                  {formatWalletMoney(
                    posting.reservedDelta,
                    withdrawal.currencyCode,
                    withdrawal.currencyMinorUnit,
                  )}{' '}
                  ·{' '}
                  {formatWalletMoney(
                    posting.withdrawnDelta,
                    withdrawal.currencyCode,
                    withdrawal.currencyMinorUnit,
                  )}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {withOffset(posting.effectiveAt, language)}
                </Typography>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Section>
      {permissions.has('AUDIT_VIEW') ? (
        <MuiLink
          component={Link}
          to={`${paths.audit}?entityType=AFFILIATE_WITHDRAWAL&entityId=${withdrawal.id}`}
        >
          {t('withdrawals.links.audit')}
        </MuiLink>
      ) : null}
    </Stack>
  );
}

function Section({ children, title }: { readonly children: ReactNode; readonly title: string }) {
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" sx={{ mb: 2 }} variant="h5">
        {title}
      </Typography>
      {children}
    </Paper>
  );
}
function withOffset(value: string, language: TimeLanguage) {
  const time = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${time.display} (${time.originalOffset})`;
}
function nullableTime(value: string | null, language: TimeLanguage, fallback: string) {
  return value === null ? fallback : withOffset(value, language);
}
